import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Grow,
  Link,
  Paper,
  Popper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { CSVLink } from "react-csv";
import { Data, LabelKeyObject } from "react-csv/lib/core";
import { zipFileDownload } from "../common/services/Utilities";
import { AppUrl } from "../config";
import { KeyValue, ZipFile } from "../interfaces";

const PREFIX = "DataDownload";

const classes = {
  modalDownload: `${PREFIX}-modalDownload`,
  CSVLink: `${PREFIX}-CSVLink`,
  dialog: `${PREFIX}-dialog`,
  dialogButton: `${PREFIX}-dialogButton`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogContent: `${PREFIX}-dialogContent`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.modalDownload}`]: {
    "position": "relative",
    "margin": "0 auto 1rem auto",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "height": 32,
    "width": 170,
    "border": "1px solid #ACC8DF",
    "borderRadius": 4,
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "backgroundColor": theme.palette.secondary.main,
    "&:hover, &:focus-visible, &:focus": {
      textDecoration: "none",
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
    },
  },

  [`& .${classes.CSVLink}`]: {
    "fontSize": "1rem",
    "fontWeight": 400,
    "lineHeight": 1.5,
    "width": "100%",
    "color": theme.palette.primary.dark,
    "display": "block",
    "padding": "6px 16px",
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "none",
    },
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "none",
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      textDecoration: "none",
    },
  },
  [`& .${classes.dialog}`]: {
    '& div[role="dialog"]': {
      padding: "32px 32px 8px 32px",
      maxWidth: "500px",
    },
    "& .MuiDialogContentText-root": {
      marginBottom: 0,
      paddingBottom: 0,
    },
    "& .MuiDialogContent-root": {
      padding: 0,
    },
  },

  [`& .${classes.dialogButton}`]: {
    "width": 170,
    "display": "flex",
    "&#cancel-download, &#continue-download": {
      "margin": "0 auto 1rem auto",
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
      },
      "&:active": {
        backgroundColor: "#ffffff",
        color: theme.palette.primary.main,
      },
    },
  },

  [`& .${classes.dialogTitle}`]: {
    "textAlign": "center",
    "& > h2": {
      color: "#1A4480",
      fontSize: 24,
    },
  },

  [`& .${classes.dialogContent}`]: {
    padding: 0,
  },
}));

interface Props {
  apiDownload?: APIDownload[];
  loading?: boolean;
  downloadLinks?: LinkDownload[];
  apiDownloadAll?: APIDownload[];
  numberOfResults?: number;
  downloadLimit?: number;
  appState?: KeyValue;
}

export interface APIDownload {
  data: string | KeyValue[];
  fileName: string;
  onClick: (e: any) => void;
  id?: string;
  linkText?: string;
  headers?: LabelKeyObject[] | string[];
  additionalContent?: string[][];
  foontones?: string[];
  downloadImageFn?: () => {} | null;
}

interface LinkDownload {
  href: string;
  text: string;
  id?: string;
}

export default function DataDownload({
  apiDownload,
  loading,
  downloadLinks,
  numberOfResults,
  downloadLimit,
  appState,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [downloadIndex, setDownloadIndex] = React.useState<number>(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    if (open && openModal) {
      if (loading && !downloading) {
        setDownloading(true);
      }
      if (downloading && !loading) {
        setDownloading(false);
        setOpen(false);
        setOpenModal(false);
      }
    }
  }, [downloading, loading, open, openModal]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenModal(false);
  };

  function getData(e: any, index: number) {
    e.preventDefault();
    setDownloadIndex(index);
    setOpenModal(true);
  }

  const handleClick = () => {
    const stringArray: ZipFile[] = [
      {
        url: `${AppUrl("API")}/institutions_definitions.csv`,
        name: "institutions_definitions",
      },
      {
        url: `${AppUrl("API")}/events_definitions.csv`,
        name: "events_definitions",
      },
      {
        url: `${AppUrl("API")}/locations_definitions.csv`,
        name: "locations_definitions",
      },
    ];
    zipFileDownload(stringArray, "variable_definitions");
    handleClose();
  };

  return (
    <>
      <Button
        ref={anchorRef}
        id="dataDownload"
        aria-label="Download Options"
        aria-controls={open ? "dataDownloadMenu" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        variant="contained"
        color="secondary"
        size={isMobile ? "large" : "medium"}
        fullWidth={isMobile}
        disabled={loading}
        sx={(theme) => ({
          "height": 32,
          "width": 160,
          "minWidth": 40,
          "textAlign": "center",
          "lineHeight": 1,
          "& svg": {
            color: theme.palette.primary.main,
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: 0,
            right: 0,
            margin: "auto",
            textAlign: "center",
          },
          "&:hover": {
            backgroundColor: "#e0e0e0",
          },
          "&:focus": {
            backgroundColor: "#e0e0e0",
          },
          "&:active": {
            backgroundColor: "#c7c7c7",
          },
        })}
      >
        {loading && <CircularProgress disableShrink />}
        Download Results
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="left"
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 10 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <StyledPaper
              sx={{
                marginRight: "5px",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div id="dataDownloadMenu">
                  {apiDownload
                    ? apiDownload.map((data, index) => {
                        const downloadData =
                          downloadIndex && openModal
                            ? (apiDownload[downloadIndex - 1].data as Data)
                            : (data.data as Data);

                        let filteredData: any = downloadData.filter(
                          (responseData: KeyValue) =>
                            responseData !== undefined,
                        );

                        const downloadModal =
                          downloadLimit &&
                          numberOfResults &&
                          numberOfResults > downloadLimit;

                        const csvDownload = (bypass?: boolean) => {
                          const downloadData: KeyValue =
                            downloadIndex && openModal
                              ? apiDownload[downloadIndex - 1]
                              : data;

                          const footnotes: string[] = downloadData.footnotes
                            ?.length
                            ? downloadData.footnotes
                            : [];
                          if (downloadData.additionalContent) {
                            const headers = downloadData?.headers.map(
                              (header: KeyValue) => header.label,
                            );
                            const headerKeys = downloadData?.headers.map(
                              (header: KeyValue) => header.key,
                            );

                            const newfilteredData: any = [];
                            filteredData.forEach((data: KeyValue) => {
                              const array = headerKeys.map(
                                (key: string) => data[key],
                              );
                              // Check to see if Cert or Namefull (For Totals Row) exists
                              if (array[0] || array[2]) {
                                newfilteredData.push(array);
                              }
                            });
                            if (newfilteredData[0]) {
                              filteredData = newfilteredData;
                            }

                            if (
                              Array.isArray(filteredData[0]) &&
                              filteredData[0].toString() !==
                                downloadData.additionalContent[0].toString()
                            ) {
                              const headerRow = footnotes.length
                                ? [...headers, ""]
                                : headers;
                              const lastIndex = headerRow.length;
                              const footnoteRow = footnotes.length
                                ? [
                                    ...Array(lastIndex - 1).fill(""),
                                    footnotes.join(" "),
                                  ]
                                : undefined;

                              filteredData = [
                                ...downloadData.additionalContent,
                                [],
                                headerRow,
                                ...filteredData,
                                ...(footnoteRow ? [footnoteRow] : []),
                              ];
                            }
                          } else if (footnotes.length) {
                            const headerObjects = downloadData?.headers;
                            const headerKeys = headerObjects
                              ? headerObjects.map(
                                  (header: KeyValue) => header.key,
                                )
                              : Object.keys(filteredData[0] || {});
                            const headerRow = headerObjects
                              ? headerObjects.map(
                                  (header: KeyValue) => header.label,
                                )
                              : headerKeys;

                            if (!Array.isArray(filteredData[0])) {
                              const dataRows = filteredData.map(
                                (row: KeyValue) => [
                                  ...headerKeys.map((key: string) => row[key]),
                                  "",
                                ],
                              );
                              const lastIndex = headerKeys.length + 1;
                              const footnoteRow = [
                                ...Array(lastIndex - 1).fill(""),
                                footnotes.join(" "),
                              ];
                              filteredData = [
                                [...headerRow, ""],
                                ...dataRows,
                                footnoteRow,
                              ];
                            }
                          }
                          return data.downloadImageFn !== undefined ? (
                            <Button
                              className={
                                bypass ? classes.modalDownload : classes.CSVLink
                              }
                              onClick={() =>
                                data.downloadImageFn && data.downloadImageFn()
                              }
                            >
                              {data.linkText}
                            </Button>
                          ) : (
                            <CSVLink
                              className={
                                bypass ? classes.modalDownload : classes.CSVLink
                              }
                              id={
                                bypass ? "continue-download" : downloadData.id
                              }
                              headers={
                                downloadData.additionalContent ||
                                footnotes.length
                                  ? undefined
                                  : downloadData.headers
                              }
                              filename={downloadData.fileName}
                              data={filteredData}
                              asyncOnClick={true}
                              onClick={(e: any) => {
                                if (!downloadModal || bypass) {
                                  downloadData.onClick(e);
                                } else {
                                  getData(e, index + 1);
                                }
                              }}
                            >
                              {bypass ? (
                                <Button
                                  className={classes.dialogButton}
                                  onClick={handleClose}
                                  color="primary"
                                  variant="contained"
                                  id="continue-download-btn"
                                  sx={{
                                    "borderSpacing": "12px",
                                    "width": 170,
                                    "display": "flex",
                                    "&#cancel-download, &#continue-download": {
                                      "margin": "0 auto 1rem auto",
                                      "&:hover, &:focus": {
                                        backgroundColor: "#ffffff",
                                        color: "#1A4480",
                                      },
                                      "&:active": {
                                        backgroundColor: "#ffffff",
                                        color: "#1A4480",
                                      },
                                    },
                                  }}
                                >
                                  Continue
                                </Button>
                              ) : data.linkText ? (
                                data.linkText
                              ) : (
                                numberOfResults !== 0 && "Download Results Data"
                              )}
                              {bypass && loading && (
                                <CircularProgress
                                  style={{ position: "absolute", right: 14 }}
                                  disableShrink
                                  size={20}
                                  color="secondary"
                                />
                              )}
                            </CSVLink>
                          );
                        };

                        const largeDownloadAlert = (
                          <Dialog
                            id="large-download-alert"
                            className={classes.dialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            open={openModal}
                            sx={{
                              '& div[role="dialog"]': {
                                padding: "32px 32px 8px 32px",
                                maxWidth: "500px",
                              },
                              "& .MuiDialogContentText-root": {
                                marginBottom: 0,
                                paddingBottom: 0,
                              },
                              "& .MuiDialogContent-root": {
                                padding: 0,
                              },
                            }}
                          >
                            <DialogContent>
                              <DialogContentText
                                variant="body2"
                                color="primary.main"
                                id="alert-dialog-description"
                              >
                                You have requested a large data download of over{" "}
                                {downloadLimit && downloadLimit > 999
                                  ? `${downloadLimit / 1000}k`
                                  : downloadLimit}{" "}
                                records. This will take longer to complete than
                                usual. Please be patient while we process your
                                request.
                              </DialogContentText>
                              <DialogTitle
                                className={classes.dialogTitle}
                                id="alert-dialog-title"
                                sx={{
                                  "textAlign": "center",
                                  "& > h2": {
                                    color: "#1A4480",
                                    fontSize: 24,
                                  },
                                }}
                              >
                                Do you wish to continue?
                              </DialogTitle>
                            </DialogContent>
                            <DialogActions
                              sx={{
                                display: "block",
                              }}
                            >
                              <Grid container>
                                <Grid
                                  size={{
                                    sm: 6,
                                    xs: 12,
                                  }}
                                >
                                  <Button
                                    className={classes.dialogButton}
                                    onClick={handleClose}
                                    color="secondary"
                                    variant="contained"
                                    id="cancel-download"
                                    sx={{
                                      "width": 170,
                                      "display": "flex",
                                      "&#cancel-download, &#continue-download":
                                        {
                                          "margin": "0 auto 1rem auto",
                                          "&:hover, &:focus": {
                                            backgroundColor: "#1A4480",
                                            color: "#ffffff",
                                          },
                                          "&:active": {
                                            backgroundColor: "#ffffff",
                                            color: "#1A4480",
                                          },
                                        },
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Grid>
                                <Grid
                                  size={{
                                    sm: 6,
                                    xs: 12,
                                  }}
                                >
                                  {csvDownload(true)}
                                </Grid>
                              </Grid>
                            </DialogActions>
                          </Dialog>
                        );

                        return (
                          <React.Fragment key={index}>
                            {downloadModal && openModal && largeDownloadAlert}
                            {csvDownload()}
                          </React.Fragment>
                        );
                      })
                    : undefined}
                  {downloadLinks
                    ? downloadLinks.map((link: LinkDownload, int: number) => {
                        return (
                          <div key={int}>
                            {window.location.pathname.includes("details") ? (
                              <Button
                                style={{
                                  margin: 0,
                                  lineHeight: 0,
                                  borderRadius: 0,
                                }}
                                key={int}
                                className={classes.CSVLink}
                                onClick={handleClick}
                              >
                                {link.text}
                              </Button>
                            ) : (
                              <Link
                                key={int}
                                id={link.id}
                                href={link.href}
                                className={classes.CSVLink}
                                sx={{ textDecoration: "none" }}
                                onClick={handleClose}
                              >
                                {link.text}
                              </Link>
                            )}
                          </div>
                        );
                      })
                    : undefined}
                </div>
              </ClickAwayListener>
            </StyledPaper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
